import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useDocumentTitle } from '../hooks/core';
import { useAuth } from '../hooks';

export const VerifyEmailPage: React.FC = () => {
  useDocumentTitle('Verify Email Change');
  const { token } = useParams<{ token: string }>();
  const { refreshUser } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Invalid verification link');
        setIsLoading(false);
        return;
      }

      try {
        await authApi.verifyEmailChange(token);
        setSuccess(true);
        // Refresh user data to get new email
        await refreshUser();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to verify email. The link may have expired or already been used.');
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [token, refreshUser]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="text.secondary">
          Verifying your email change...
        </Typography>
      </Box>
    );
  }

  if (success) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Card sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent>
            <Typography variant="h5" component="h1" gutterBottom align="center">
              Email Changed Successfully
            </Typography>

            <Alert severity="success" sx={{ mb: 2 }}>
              Your email address has been updated.
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your account is now using your new email address. You can use it to log in going forward.
            </Typography>

            <Button
              component={RouterLink}
              to="/profile"
              fullWidth
              variant="contained"
            >
              Go to Profile
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <Card sx={{ maxWidth: 400, width: '100%' }}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Verification Failed
          </Typography>

          <Alert severity="error" sx={{ mb: 2 }}>
            {error || 'Unable to verify email change.'}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The verification link may have expired or already been used. Email change links expire after 24 hours.
          </Typography>

          <Button
            component={RouterLink}
            to="/profile"
            fullWidth
            variant="contained"
            sx={{ mb: 2 }}
          >
            Go to Profile Settings
          </Button>

          <Typography variant="body2" color="text.secondary" align="center">
            You can request a new email change from your profile settings.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
